import { useCallback, memo, SyntheticEvent } from 'react';
import {
  Panel, PanelHeader, Tabs, Tab, useTranslations, RenderIf,
} from '@uniqkey-frontend/shared-app';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useUser } from '../../../../contexts/UserContext';
import { useGetOrganizationById, useGetGroupById } from '../../../../hooks/reactQuery';
import PageRouteEnum from '../../../../enums/PageRouteEnum';
import ACLEnum from '../../../../enums/ACLEnum';
import { publishTablePreservationReset } from '../../../../helpers/tablePreservation/reset';
import OrganizationDetailsTab from '../../../OrganizationPage/components/OrganizationDetailsTab';
import OrganizationVerifiedDomainsTab from
  '../../../OrganizationPage/components/OrganizationVerifiedDomainsTab';
import OrganizationAdminsTab from '../../../OrganizationPage/components/OrganizationAdminsTab';
import OrganizationLicensesHistoryTab from
  '../../../OrganizationPage/components/OrganizationLicensesHistoryTab';
import OrganizationEmployeesTab from
  '../../../OrganizationPage/components/OrganizationEmployeesTab';
import OrganizationAuditLogsTab
  from '../../../OrganizationPage/components/OrganizationAuditLogsTab';

export enum GroupOrganizationModuleTabEnum {
  Details = 'details',
  Domains = 'domains',
  Admins = 'admins',
  Licenses = 'licenses',
  Employees = 'employees',
  AuditLogs = 'audit'
}
interface IGroupOrganizationModuleProps {
  activeTab: GroupOrganizationModuleTabEnum;
}
interface IGroupOrganizationModuleContext {
  groupId: string;
  organizationId: string;
}

const GroupOrganizationModule = (props: IGroupOrganizationModuleProps) => {
  const { activeTab } = props;
  const { t } = useTranslations();
  const { userCan } = useUser();
  const context = useOutletContext<IGroupOrganizationModuleContext>();
  const navigate = useNavigate();

  const {
    groupId,
    organizationId,
  } = context;

  const { isLoading: isGroupLoading } = useGetGroupById(
    { groupId },
  );

  const { isLoading: isOrganizationLoading } = useGetOrganizationById(
    { organizationId },
  );

  const isLoading = isGroupLoading || isOrganizationLoading;

  const handleTabClick = useCallback((
    event: SyntheticEvent,
    nextActiveTab: GroupOrganizationModuleTabEnum,
  ) => {
    const nextPath = `${PageRouteEnum.Groups}/${groupId}/${organizationId}/${nextActiveTab}`;
    if (activeTab !== nextActiveTab) {
      navigate(nextPath);
      return;
    }
    navigate(nextPath, { replace: true });
    publishTablePreservationReset();
  }, [groupId, organizationId, activeTab, navigate]);

  const hidePanelHeaderBottomDivider = activeTab === GroupOrganizationModuleTabEnum.Details;

  const canViewDomains = userCan(ACLEnum.OrganizationViewDomains);
  const canViewAdmins = userCan(ACLEnum.OrganizationViewAdmins);
  const canViewLicensesHistory = userCan(ACLEnum.OrganizationViewLicenseHistory);
  const canViewEmployees = userCan(ACLEnum.EmployeeAccountView);
  const canViewAuditLogs = userCan(ACLEnum.OrganizationViewAuditLogs);

  return (
    <Panel className="height-100-percent flex-column">
      <PanelHeader px={3} hideBottomDivider={hidePanelHeaderBottomDivider}>
        <Tabs value={activeTab} isLoading={isLoading}>
          <Tab
            label={t('organizationPage.tabs.organizationDetails')}
            value={GroupOrganizationModuleTabEnum.Details}
            onClick={handleTabClick}
          />
          {canViewDomains && (
            <Tab
              label={t('organizationPage.tabs.verifiedDomains')}
              value={GroupOrganizationModuleTabEnum.Domains}
              onClick={handleTabClick}
            />
          )}
          {canViewAdmins && (
            <Tab
              label={t('organizationPage.tabs.admins')}
              value={GroupOrganizationModuleTabEnum.Admins}
              onClick={handleTabClick}
            />
          )}
          {canViewLicensesHistory && (
            <Tab
              label={t('organizationPage.tabs.licensesHistory')}
              value={GroupOrganizationModuleTabEnum.Licenses}
              onClick={handleTabClick}
            />
          )}
          {canViewEmployees && (
            <Tab
              label={t('organizationPage.tabs.employees')}
              value={GroupOrganizationModuleTabEnum.Employees}
              onClick={handleTabClick}
            />
          )}
          {canViewAuditLogs && (
            <Tab
              label={t('organizationPage.tabs.auditLogs')}
              value={GroupOrganizationModuleTabEnum.AuditLogs}
              onClick={handleTabClick}
            />
          )}
        </Tabs>
      </PanelHeader>
      <RenderIf condition={activeTab === GroupOrganizationModuleTabEnum.Details}>
        <OrganizationDetailsTab organizationId={organizationId} />
      </RenderIf>
      <RenderIf condition={canViewDomains && activeTab === GroupOrganizationModuleTabEnum.Domains}>
        <OrganizationVerifiedDomainsTab organizationId={organizationId} />
      </RenderIf>
      <RenderIf condition={canViewAdmins && activeTab === GroupOrganizationModuleTabEnum.Admins}>
        <OrganizationAdminsTab organizationId={organizationId} />
      </RenderIf>
      <RenderIf
        condition={canViewLicensesHistory && activeTab === GroupOrganizationModuleTabEnum.Licenses}
      >
        <OrganizationLicensesHistoryTab organizationId={organizationId} />
      </RenderIf>
      <RenderIf
        condition={canViewEmployees && activeTab === GroupOrganizationModuleTabEnum.Employees}
      >
        <OrganizationEmployeesTab organizationId={organizationId} groupId={groupId} />
      </RenderIf>
      <RenderIf
        condition={canViewAuditLogs && activeTab === GroupOrganizationModuleTabEnum.AuditLogs}
      >
        <OrganizationAuditLogsTab organizationId={organizationId} />
      </RenderIf>
    </Panel>
  );
};

export default memo(GroupOrganizationModule);
